import React from 'react'
import styled from 'styled-components'

import {
    invoiceGeneratorActioned,
    BigformActionedAction,
    InvoiceGeneratorActionedField,
} from '@/analytics/segment'
import Button from '@/components/button'
import {HubspotForm} from '@/components/hubspot-form'
import {Modal} from '@/components/modal'
import {Text} from '@/components/text'
import useGeneratePdf from '@/hooks/use-generate-pdf'
import {useGlobalStrings} from '@/providers/global-strings'
import {tokens} from '@/theme'
import getEnvironmentField from '@/utils/get-environment-field'
import replacePlaceholders from '@/utils/replace-placeholders'

import type {State} from './state'
import type {Props as ModalProps} from '@/components/modal'
import type {InvoiceGeneratorFragment} from '@/graphql-types'
import type {FormConfig} from '@/types/consents'

type ArrayElement<ArrayType extends readonly unknown[]> = ArrayType[number]

interface Props extends Omit<ModalProps, 'children'> {
    getYourInvoiceModal: ArrayElement<InvoiceGeneratorFragment['data']['get_your_invoice_modal']>
    thankYouModal: ArrayElement<InvoiceGeneratorFragment['data']['thank_you_modal']>
    onReset: () => void
    state: State
}

export const GetYourInvoiceModal = ({
    getYourInvoiceModal,
    thankYouModal,
    isOpen,
    onDismiss,
    onReset,
    state,
}: Partial<Props>) => {
    const [isModalFormSubmitted, setIsModalFormSubmitted] = React.useState(false)
    const [userEmail, setUserEmail] = React.useState('')
    const globalStrings = useGlobalStrings()
    const {handleGeneratePdf, pdfLink, loading, error} = useGeneratePdf({
        service: 'invoice-generator',
        state,
        errorMessage: globalStrings.something_went_wrong,
    })

    const modalTitle = isModalFormSubmitted
        ? thankYouModal?.modal_title
        : getYourInvoiceModal?.modal_title

    const modalSubtitle = isModalFormSubmitted
        ? (replacePlaceholders(thankYouModal?.modal_subtitle ?? '', {
              '{{email}}': userEmail,
          }) as unknown as string)
        : getYourInvoiceModal?.modal_subtitle

    const modalImageUrl = isModalFormSubmitted
        ? thankYouModal?.modal_image.fluid.base64
        : getYourInvoiceModal?.modal_image.fluid.base64

    const onNewInvoice = () => {
        setIsModalFormSubmitted(false)
        onReset?.()
    }

    const onInitialSubmit = async () => ({
        pdfUrl: await handleGeneratePdf(),
    })

    const onSubmitSuccess = (state: {email: string}) => {
        setUserEmail(state.email)
        setIsModalFormSubmitted(true)

        invoiceGeneratorActioned({
            action: BigformActionedAction.Completed,
            field: InvoiceGeneratorActionedField.EmailSubmitted,
        })
    }

    return (
        <Modal
            title={modalTitle}
            subtitle={modalSubtitle}
            image={{
                url: modalImageUrl ?? '',
                dimensions: {
                    width: 80,
                    height: 80,
                },
                copyright: null,
                alt: null,
            }}
            isOpen={isOpen}
            onDismiss={onDismiss}
            overlayBgColor="pink"
        >
            {isModalFormSubmitted ? (
                <CTAWrapper>
                    <Button
                        loading={loading}
                        as="a"
                        href={pdfLink}
                        download={`Invoice ${state?.invoiceNumber}`}
                        onClick={() =>
                            invoiceGeneratorActioned({
                                action: BigformActionedAction.Interacted,
                                field: InvoiceGeneratorActionedField.PdfDownloaded,
                            })
                        }
                    >
                        {thankYouModal?.download_pdf_button_text}
                    </Button>
                    {error && (
                        <Text $variant="label-base" $color="error">
                            {error}
                        </Text>
                    )}
                    <Button $variant="outline" onClick={onNewInvoice}>
                        {thankYouModal?.new_invoice_button_text}
                    </Button>
                </CTAWrapper>
            ) : (
                <>
                    <StyledHubspotForm
                        formConfig={
                            getEnvironmentField({
                                production: getYourInvoiceModal?.form_production,
                                staging: getYourInvoiceModal?.form_staging,
                            }) as FormConfig
                        }
                        content={{
                            submitButtonText: globalStrings.submit,
                        }}
                        hiddenFields={[
                            'companySize',
                            'country',
                            'firstName',
                            'lastName',
                            'phoneNumber',
                            'discoveredVia',
                        ]}
                        shouldRedirect={false}
                        onBeforeSubmit={onInitialSubmit}
                        onSubmitSuccess={onSubmitSuccess}
                    />
                </>
            )}
        </Modal>
    )
}

const StyledHubspotForm = styled(HubspotForm)`
    padding: 0;
    row-gap: ${tokens.spacing24};
`

const CTAWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${tokens.spacing20};
`
